<script setup lang="ts" generic="T">
    // @ts-nocheck

    import { CALENDAR_VIEWS, SPARRAD_TID } from "@/components/calendars/helpers";
    import VardplatsSparradTidTimelineEvent from "@/tabs/Vardplatsbokning/VardplatsSparradTidTimelineEvent.vue";
    import VardplatsTimelineEvent from "@/tabs/Vardplatsbokning/VardplatsTimelineEvent.vue";
    import {
        AllDayContentArg,
        AllowFunc,
        CalendarOptions,
        CustomContentGenerator,
        DatesSetArg,
        EventClickArg,
        EventDropArg,
        EventInput,
        EventSourceInput,
    } from "@fullcalendar/core";
    import { EventImpl } from "@fullcalendar/core/internal";
    // @ts-ignore
    import svLocale from "@fullcalendar/core/locales/sv";
    import dayGridPlugin from "@fullcalendar/daygrid";
    // @ts-ignore
    import interactionPlugin, {
        DateClickArg,
        DropArg,
        EventDragStopArg,
        EventLeaveArg,
        EventReceiveArg,
        EventResizeDoneArg,
    } from "@fullcalendar/interaction";
    // @ts-ignore
    import {
        ColCellContentArg,
        ResourceApi,
        ResourceInput,
        ResourceLabelContentArg,
        ResourceLaneContentArg,
    } from "@fullcalendar/resource";
    // @ts-ignore
    import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
    // @ts-ignore
    import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
    // @ts-ignore
    import timeGridPlugin from "@fullcalendar/timegrid";
    // @ts-ignore
    import FullCalendar from "@fullcalendar/vue";
    import { computed, ref, toRefs } from "vue";

    interface CalendarProps {
        initialView: keyof typeof CALENDAR_VIEWS;
        // Min tid på events
        minTime?: string;
        // Max tid på events
        maxTime?: string;
        // Ska det gå att släppa events utifrån på kalendern?
        droppable?: boolean;
        // Ska events i kalendern vara redigerbara?
        editable?: boolean;
        // Ska helger visas och användas i kalendern?
        weekends?: boolean;
        // Funktion eller sträng som renderas till vänster i raden för heldagsevents
        allDayContent?: string | (() => CustomContentGenerator<AllDayContentArg>);
        // Spec på sortering av resurser
        resourceOrder?: string;
        // Dagar som ska döljas
        hiddenDays?: number[];
        // Alla events
        events: EventInput[];
        // Alla resurser
        resources?: ResourceInput[];
        // Ska duration kunna redigeras på events?
        eventDurationEditable?: boolean;
        // Ska start kunna redigeras på events?
        eventStartEditable?: boolean;
        // Ska resurs kunna redigeras på events?
        eventResourceEditable?: boolean;
        // Funktion som anropas för att se om eventet kan släppas över en specifik del av kalendern
        eventAllow?: AllowFunc;
        /*
         Data från BE som använts för att skapa events och kan användas för att hämta det som inte ligger lagrat i eventet
         */
        data: Map<string, T>;
        /*
         Data från BE som använts för att skapa spärrade events och kan användas för att hämta det som inte ligger lagrat i eventet
         */
        sparradeTiderData?: Map<string, T>;
        // Namn på variabel som används för att avgöra färgen som ska ritas ut i event
        colorKey?: string;
        // Innehåll till vänster i resursvyer
        resourceAreaHeaderContent?: string;
        // Form data från filtret som avgör vilka events som ska visas i kalendern
        filter?: { [key: string]: unknown };
        // Ska veckonummer visas?
        showWeekNumbers?: boolean;
        // Namn och value på variabel som ska ändras när event utifrån släpps på kalendern
        dropAreaSet?: {
            name: string;
            value: unknown;
        };
        // Id på drop area. Ska vara samma som det som skickats till CalendarDropArea
        dropAreaId?: string;
        /*
            Objekt med key som är namn på möjligt namn som kan användas som color key och Map som innehåller
            key id som ska kollas mot och value index som ska användas vid hämtning av färg
            T.ex: { vardgivare: new Map([["abcdef", 1], ["glqer", 7]]) }
         */
        colorKeyIndices?: { [key: string]: Map<string, number> };
        // Funktion som anropas när man ändrat storlek på eventet genom att dra i kanterna
        eventResize?: (arg: EventResizeDoneArg) => void;
    }

    const emit = defineEmits<{
        (
            event: "eventClick",
            payload: {
                id: string;
            }
        ): void;
        (event: "dateChange", payload: DatesSetArg): void;
        (
            event: "externalDrop",
            payload: {
                event: EventSourceInput;
                revert: () => void;
                success: () => void;
            }
        ): void;
        (
            event: "eventAdd",
            payload: {
                event: EventImpl;
                success: () => void;
                revert: () => void;
            }
        ): void;
        (event: "eventDrop", payload: EventDropArg): void;
        (event: "eventResize", payload: EventResizeDoneArg): void;
        (
            event: "panelAction",
            payload: {
                action: string;
                data: any;
            }
        ): void;
    }>();

    const props = withDefaults(defineProps<CalendarProps>(), {
        minTime: "00:00:00",
        maxTime: "24:00:00",
        allDayContent: "",
        resourceOrder: "ordning,namn",
        droppable: true,
        editable: true,
        eventDurationEditable: true,
        eventStartEditable: true,
        eventResourceEditable: true,
        resourceAreaHeaderContent: "Resurser",
        showWeekNumbers: false,
    });

    const {
        initialView,
        droppable,
        minTime,
        maxTime,
        weekends,
        allDayContent,
        resourceOrder,
        hiddenDays,
        events,
        resources,
        eventDurationEditable,
        eventStartEditable,
        editable,
        eventResourceEditable,
        eventAllow,
        data,
        sparradeTiderData,
        colorKey,
        resourceAreaHeaderContent,
        showWeekNumbers,
        dropAreaSet,
        dropAreaId,
    } = toRefs(props);

    const calendarRef = ref();

    defineExpose({
        // Hämtning av calendar api
        getApi: () => calendarRef.value?.getApi?.(),
    });

    const isEventAllowed: AllowFunc = (dropInfo, ...props) => {
        if (eventAllow?.value) {
            return eventAllow.value(dropInfo, ...props);
        }

        return true;
    };

    /**
     * Anropas när event utifrån släpps på kalendern
     * @param receiveInfo
     */
    const onReceive = (receiveInfo: EventReceiveArg) => {
        if (receiveInfo.event.extendedProps.restrictedDates) {
            receiveInfo.event.setStart(new Date(receiveInfo.event.extendedProps.restrictedDates.start), {
                maintainDuration: true,
            });
        }

        emit("externalDrop", { event: receiveInfo.event, revert: receiveInfo.revert, success: () => {} });
    };

    /**
     * Anropas vid klick av event
     * @param eventClickInfo
     */
    const onEventClick = (eventClickInfo: EventClickArg) => {
        emit("eventClick", { id: eventClickInfo.event.id });
    };

    /**
     * Anropas när ett event slutar dras (implicit släpps). Det behöver användas för att fånga upp events som släpps
     * på CalendarDropArea
     * @param info
     */
    const onEventDragStop = (info: EventDragStopArg) => {
        // Om dropareaid satts och eventet släppts på CalendarDropArea
        if (dropAreaId?.value) {
            const isDropArea = (
                info.jsEvent.target as EventTarget & { matches: (selector: string) => boolean }
            )?.matches(`#${dropAreaId.value}, #${dropAreaId.value} *`);
            const event = info.event as EventImpl & {
                setResources: (resources: string | ResourceApi[]) => void;
                getResources: () => Array<{ id: string }>;
            };

            if (isDropArea && dropAreaSet?.value?.name === "resources") {
                // @ts-ignore
                const oldResource = event.getResources()?.[0]?.id ?? null;
                // @ts-ignore
                event.setResources(dropAreaSet.value.value);

                emit("externalDrop", {
                    event,
                    success: () => {},
                    revert: () => {
                        // @ts-ignore
                        event.setResources?.(oldResource);
                    },
                });
            }
        }
    };

    const addResourceLaneClasses = ({ resource }: ResourceLaneContentArg) => {
        return resource.extendedProps?.laneClasses ?? [];
    };

    const addResourceLabelClasses = ({ resource }: ResourceLabelContentArg) => {
        return resource?.extendedProps?.labelClasses ?? [];
    };

    const onEventDrop = (arg: EventDropArg) => {
        emit("eventDrop", arg);
    };

    const onEventResize = (arg: EventResizeDoneArg) => {
        emit("eventResize", arg);
    };

    const panelAction = (payload: { action: string; data: any }) => {
        emit("panelAction", payload);
    };

    const getSparradTidData = (event: EventImpl) => {
        const id = event.id.replace(SPARRAD_TID, "");

        const value = sparradeTiderData.value?.get(isNaN(Number(id)) ? id : Number(id));

        return value;
    };

    const calendarOptions = computed<CalendarOptions>(() => ({
        // Hårdkodate options
        views: CALENDAR_VIEWS as CalendarOptions["views"],
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, resourceTimeGridPlugin, resourceTimelinePlugin],
        // @ts-ignore
        schedulerLicenseKey: import.meta.env.VITE_FULLCALENDAR_SCHEDULER_LICENSE_KEY || "",
        headerToolbar: false,
        locale: svLocale,
        allDayClassNames: "all-day",
        weekNumbers: showWeekNumbers.value,
        snapDuration: "00:05",
        nowIndicator: true,
        resourceAreaWidth: "20ch",
        resourceAreaHeaderContent: resourceAreaHeaderContent.value,
        firstDay: 1,
        height: "100%",
        eventColor: "transparent",
        eventBackgroundColor: "transparent",
        eventBorderColor: "transparent",
        dragRevertDuration: 0,
        // Förändringsbara options
        droppable: droppable.value,
        editable: editable.value,
        eventStartEditable: eventStartEditable.value,
        eventDurationEditable: eventDurationEditable.value,
        eventResourceEditable: eventResourceEditable.value,
        hiddenDays: hiddenDays?.value ?? [],
        initialView: initialView.value,
        weekends: weekends?.value,
        slotMinTime: minTime.value,
        slotMaxTime: maxTime.value,
        allDayContent: typeof allDayContent.value === "function" ? allDayContent.value() : allDayContent.value,
        resourceOrder: resourceOrder.value,
        events: events.value,
        resources: resources?.value ?? [],
        // Funktioner
        eventAllow: isEventAllowed,
        eventReceive: onReceive,
        eventClick: onEventClick,
        eventDragStop: onEventDragStop,
        resourceLaneClassNames: addResourceLaneClasses,
        resourceLabelClassNames: addResourceLabelClasses,
        eventDrop: onEventDrop,
        eventResize: onEventResize,
    }));
</script>

<template>
    <div class="base-calendar">
        <FullCalendar id="fullcal" ref="calendarRef" :options="calendarOptions">
            <template #eventContent="{ event, view }">
                <VardplatsSparradTidTimelineEvent
                    v-if="view.type === 'avdelningDayTimeline' && event.id.startsWith(SPARRAD_TID)"
                    :event="event"
                    :data="getSparradTidData(event)"
                />
                <VardplatsTimelineEvent
                    v-else-if="view.type === 'avdelningDayTimeline'"
                    :event="event"
                    :data="data.get(event.id)"
                    :colorKey="colorKey"
                    :colorKeyIndices="colorKeyIndices"
                    @panelAction="panelAction"
                />
            </template>
        </FullCalendar>
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";

    .base-calendar {
        min-height: 100%;
        flex-grow: 1;

        ::v-deep .fc {
            .grey-out {
                background-color: $color-grey-background;
            }

            .fc-view-harness {
                .fc-view {
                    background: transparent;

                    .fc-scrollgrid {
                        border: none;

                        thead {
                            th,
                            td {
                                background: white;
                            }

                            tr {
                                .fc-datagrid-cell-frame {
                                    align-items: flex-end;
                                }
                            }
                        }

                        tbody {
                            .fc-timeline-header-row,
                            .fc-timeline-header-row-chrono {
                                a {
                                    color: inherit;

                                    &:hover {
                                        text-decoration: none;
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
</style>

<style lang="scss">
    // Behövs för att kunna dra och släppa events utanför kalendern
    .fc-event-dragging {
        pointer-events: none;
    }
</style>
